import { PDFDocument, type PDFPage } from 'pdf-lib';
import {
  getVisualVerticalCropHalves,
  normalizeRotation,
  type PageCropRect,
} from './getVisualVerticalCropHalves';

export type SplitProgress = {
  current: number;
  total: number;
};

export type SplitProgressCallback = (progress: SplitProgress) => void;

function applyCropBoxes(page: PDFPage, crop: PageCropRect): void {
  // MediaBox и CropBox совпадают — viewer показывает ровно половину.
  // /Rotate исходной страницы сохраняется, контент не перерисовывается.
  page.setMediaBox(crop.x, crop.y, crop.width, crop.height);
  page.setCropBox(crop.x, crop.y, crop.width, crop.height);
  page.setBleedBox(crop.x, crop.y, crop.width, crop.height);
  page.setTrimBox(crop.x, crop.y, crop.width, crop.height);
  page.setArtBox(crop.x, crop.y, crop.width, crop.height);
}

async function addSplitHalves(
  outDoc: PDFDocument,
  srcDoc: PDFDocument,
  pageIndex: number
): Promise<void> {
  const srcPage = srcDoc.getPage(pageIndex);
  const media = srcPage.getMediaBox();
  const rotation = normalizeRotation(srcPage.getRotation().angle);
  const [leftCrop, rightCrop] = getVisualVerticalCropHalves(media, rotation);

  const [leftPage] = await outDoc.copyPages(srcDoc, [pageIndex]);
  applyCropBoxes(leftPage, leftCrop);
  outDoc.addPage(leftPage);

  const [rightPage] = await outDoc.copyPages(srcDoc, [pageIndex]);
  applyCropBoxes(rightPage, rightCrop);
  outDoc.addPage(rightPage);
}

export async function splitPdfPages(
  bytes: Uint8Array,
  keep: Set<number>,
  onProgress: SplitProgressCallback
): Promise<Uint8Array> {
  const srcDoc = await PDFDocument.load(bytes, { ignoreEncryption: true });
  const pageCount = srcDoc.getPageCount();
  if (pageCount < 1) {
    throw new Error('В PDF нет страниц.');
  }

  const outDoc = await PDFDocument.create();

  for (let pageIndex = 0; pageIndex < pageCount; pageIndex += 1) {
    const pageNumber = pageIndex + 1;
    if (keep.has(pageNumber)) {
      const [copiedPage] = await outDoc.copyPages(srcDoc, [pageIndex]);
      outDoc.addPage(copiedPage);
    } else {
      await addSplitHalves(outDoc, srcDoc, pageIndex);
    }
    onProgress({ current: pageIndex + 1, total: pageCount });
  }

  return outDoc.save();
}
